import { useLocation, useNavigate, useParams } from '@tanstack/react-router';

import { Text } from '../../cdl/Text/Text';
import { AdvancedError } from '../../common/Error/AdvancedError';
import { HeaderWithLine } from '../../common/HeaderWithLine/HeaderWithLine';
import { formatDateTime } from '../../common/helpers/formatDateTime.helper';
import { formatPort } from '../../common/helpers/formatPort.helper';
import { translate } from '../../common/helpers/translate.helper';
import { useDocumentTitle } from '../../common/hooks/useDocumentTitle';
import { useRole } from '../../common/hooks/useRole';
import { useFlags } from '../../common/launchDarkly/useFlags';
import { LoadingIndicator } from '../../common/LoadingIndicator/LoadingIndicator';
import { OfferStatusTag } from '../../common/OfferStatusTag/OfferStatusTag';
import { Page } from '../../common/Page/Page';
import { PageGrid } from '../../common/PageGrid';
import { PositionDisplay } from '../../common/PositionDisplay/PositionDisplay';
import { ReadOnlyProductTable } from '../../common/ReadOnlyProductTable/ReadOnlyProductTable';
import { Section } from '../../common/Section/Section';
import { Box } from '../../common/ui/Box';
import { LubesRfqDetailBreadcrumb } from '../../offer/detail/components/LubesRfqDetailBreadcrumb';
import { OfferState } from '../../offer/model/OfferState';
import { OfferActivityTimeline } from '../../offer/OfferChat/OfferActivityTimeline';
import { OfferChatSection } from '../../offer/OfferChat/OfferChatSection';
import { SupplierOnboardingCallout } from '../../supplierOnboarding/components/SupplierOnboardingCallout';
import { useRfqDetails } from '../hooks/useRfqDetails';

import { RfqDatasheet } from './components/RfqDatasheet';
import { RfqDetailActionBar } from './components/RfqDetailActionBar';
import { RfqInvolvedParties } from './components/RfqInvolvedParties';
import { RfqOfferListSection } from './components/RfqOfferListSection';
import { RfqStatusBar } from './components/RfqStatusBar';
import { RfqStatusBarAction } from './components/RfqStatusBarAction';
import { getOpenRfqOffers } from './helpers/getOpenRfqOffers';

export const RfqDetailPage = () => {
    const { id } = useParams({ from: '/_app/requests-for-quotes/$id' });
    const navigate = useNavigate();
    const location = useLocation();
    const role = useRole();
    const flags = useFlags();

    const rfqQuery = useRfqDetails(id);
    const rfq = rfqQuery.data;

    const handleCreateQuote = () => {
        const offers = rfq?.offers ?? [];
        const openOffers = getOpenRfqOffers(offers);

        if (openOffers.length === 1 && openOffers[0].state === OfferState.ENQUIRY) {
            navigate({
                to: '/offer/$id/quote',
                params: { id: openOffers[0].id },
                state: { originLocation: location },
            });
        } else {
            navigate({
                to: '/requests-for-quotes/$id/quote',
                params: { id },
            });
        }
    };

    useDocumentTitle(
        translate('page.offerShowEnquiry', {
            vesselName: rfq?.vessel.name ?? '',
            portName: rfq?.port.name ?? '',
        })
    );

    if (rfqQuery.isPending) {
        return (
            <Page>
                <LoadingIndicator variant="page" />
            </Page>
        );
    }

    if (rfqQuery.isError || !rfq) {
        return (
            <Page>
                <AdvancedError />
            </Page>
        );
    }

    const isEnquiryState = [OfferState.ENQUIRY, OfferState.QUOTED].includes(rfq.state);
    const initialOffer = rfq.offers[0];
    const showPreQuoteChat = isEnquiryState && !!initialOffer && !initialOffer.wasQuoted();

    const viewRfqStatusBar = role.hasWriteRights(rfq.supplierId) || role.isSuperAdmin();
    const showOnboardingCallout = role.isSupplier() || role.isSuperAdmin();

    const offerListSection = (
        <RfqOfferListSection
            offers={rfq.offers ?? []}
            supplierId={rfq.supplierId}
            multipleOfferAvailability={rfq.multipleOfferAvailability}
            isEnquiryState={isEnquiryState}
            onCreateQuote={handleCreateQuote}
        />
    );

    const productSection = (
        <Box display="flex" flexDirection="column" marginBottom={16}>
            <Box display="flex" paddingBottom={16} justifyContent="space-between" alignItems="center">
                <Text variant="title" as="h2">
                    {translate('offer.detail.products')}
                </Text>
            </Box>

            <ReadOnlyProductTable items={rfq.items ?? []} sampleKits={rfq.samplekits ?? []} showPrices={false} />
        </Box>
    );

    const activityLogSection = (
        <Box display="flex" flexDirection="column" gap={48}>
            {productSection}
            {offerListSection}

            {showPreQuoteChat ? (
                <Box>
                    <Box paddingBottom={16}>
                        <Text variant="title" as="h2">
                            {translate('offer.detail.activity')}
                        </Text>
                    </Box>

                    <OfferActivityTimeline offer={initialOffer} />
                </Box>
            ) : null}
        </Box>
    );

    const legacyOfferListSection = showPreQuoteChat ? null : offerListSection;

    return (
        <Page>
            <PageGrid>
                <Box display="grid" gap={4}>
                    <LubesRfqDetailBreadcrumb />
                    <HeaderWithLine>
                        <Box
                            display="flex"
                            flexDirection="row"
                            gap={8}
                            alignItems="center"
                            justifyContent="space-between"
                            width="100%"
                        >
                            <Box display="flex" flexDirection="row" alignItems="center" gap={12}>
                                <Text variant="headline" as="h1">
                                    {rfq.orderNumber ?? translate('rfq.detail.headline')}
                                </Text>
                                <OfferStatusTag state={rfq.state} variant="supplier" kycRequested={rfq.kycRequested} />
                            </Box>
                            <Box display="flex" flexDirection="row" alignItems="center" gap={8}>
                                {role.hasWriteRights(rfq.supplierId) || role.isSuperAdmin() ? (
                                    <RfqStatusBarAction
                                        offers={rfq.offers}
                                        rfqId={id}
                                        rfqState={rfq.state}
                                        multipleOfferAvailability={rfq.multipleOfferAvailability}
                                    />
                                ) : null}
                                <RfqDetailActionBar rfq={rfq} />
                            </Box>
                        </Box>
                    </HeaderWithLine>
                </Box>

                <Box display="flex" flexDirection="column" rowGap={48} marginTop={16}>
                    {viewRfqStatusBar ? (
                        <Box display="flex" flexDirection="column" gap={16}>
                            <RfqStatusBar
                                offers={rfq.offers}
                                rfqId={id}
                                rfqState={rfq.state}
                                multipleOfferAvailability={rfq.multipleOfferAvailability}
                            />

                            {showOnboardingCallout ? (
                                <SupplierOnboardingCallout
                                    supplierId={rfq.supplierId}
                                    requestForQuoteId={id}
                                    canManage={role.isSupplier()}
                                    offers={rfq.offers}
                                />
                            ) : null}
                        </Box>
                    ) : null}

                    <Box display="grid" gridTemplateColumns="minmax(0, 75fr) minmax(0, 25fr)" gap={48}>
                        <Box display="flex" flexDirection="column" gap={flags.activityLog ? 0 : 48}>
                            <Box display="flex" paddingBottom={16} justifyContent="space-between" alignItems="center">
                                <Text variant="title" as="h2">
                                    {translate('offer.detail.details')}
                                </Text>
                            </Box>

                            <Box display="grid" gridTemplateColumns="minmax(0, 1fr) minmax(0, 1fr)" gap={48}>
                                <RfqInvolvedParties rfq={rfq} layout="vertical" />
                                <RfqDatasheet rfq={rfq} />
                            </Box>

                            {!flags.activityLog ? productSection : null}
                            {!flags.activityLog && showPreQuoteChat ? offerListSection : null}
                        </Box>

                        <Box display="flex" flexDirection="column" gap={48}>
                            {rfq.port?.latitude && rfq.port?.longitude ? (
                                <Section fullHeight={false} paddingY={0}>
                                    <PositionDisplay
                                        positionTitle={translate('offerPositions.orderPort')}
                                        primaryDescription={formatPort({ port: rfq.port })}
                                        secondaryDescription={
                                            rfq.dateDelivery
                                                ? translate('offerPositions.deliveryDate', {
                                                      date: formatDateTime({
                                                          date: rfq.dateDelivery,
                                                          format: 'PP',
                                                      }),
                                                  })
                                                : ''
                                        }
                                        latitude={rfq.port.latitude}
                                        longitude={rfq.port.longitude}
                                    />
                                </Section>
                            ) : null}

                            {!flags.activityLog && showPreQuoteChat ? <OfferChatSection offer={initialOffer} /> : null}
                        </Box>
                    </Box>

                    {flags.activityLog ? activityLogSection : legacyOfferListSection}
                </Box>
            </PageGrid>
        </Page>
    );
};
